import { defineStore } from 'pinia'
import type { CreateNotificationPayload, Notification } from '@/types/notifications'

/**
 * Generic notification store - no knowledge of specific features.
 * Any component can add notifications with custom action callbacks.
 */
export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    notifications: [] as Notification[],
  }),

  getters: {
    getUnreadCount: state => state.notifications.filter(n => !n.read).length,
    hasUnread: state => state.notifications.some(n => !n.read),
    getNotificationById: state => (id: string) => state.notifications.find(n => n.id === id),
  },

  actions: {
    addNotification(payload: CreateNotificationPayload & { id?: string }) {
      const notification: Notification = {
        read: false,
        createdAt: new Date().toISOString(),
        ...payload,
        id: payload.id || crypto.randomUUID(),
      }
      // Prevent duplicate notifications with the same id
      const existingIndex = this.notifications.findIndex(n => n.id === notification.id)
      if (existingIndex !== -1) {
        this.notifications.splice(existingIndex, 1, {
          ...notification,
          createdAt: this.notifications[existingIndex].createdAt,
        })
      } else {
        this.notifications.unshift(notification)
      }
      return notification.id
    },

    markAsRead(id: string) {
      const index = this.notifications.findIndex(n => n.id === id)
      if (index !== -1 && !this.notifications[index].sticky) {
        this.notifications.splice(index, 1, { ...this.notifications[index], read: true })
      }
    },

    markAllAsRead() {
      this.notifications = this.notifications.map(n => (n.sticky ? n : { ...n, read: true }))
    },

    removeNotification(id: string) {
      const index = this.notifications.findIndex(n => n.id === id)
      if (index !== -1) {
        this.notifications.splice(index, 1)
      }
    },

    clearAll() {
      this.notifications = []
    },
  },
})
